import { type PropsWithChildren, type ReactNode } from 'react';

import {
  Accordion,
  Box,
  Group,
  Stack,
  Text,
  useMantineTheme,
} from '@mantine/core';

import { IconChevronDown, IconPencil, IconTrash } from '@tabler/icons-react';

import type { ResponseProjectBlock } from '../../../../../../../../../shared/entities/ResponseProjectBlock';
import { useResponseProjectDetailContext } from '../../../context/responseProjectDetailContext';
import { BlockTitle } from './BlockTitle';

interface BlockWrapperProps extends PropsWithChildren {
  block: ResponseProjectBlock;
  assistantTrigger?: ReactNode;
}

export function BlockWrapper({ children, ...props }: BlockWrapperProps) {
  const hasChildren = props.block.hasChildren();

  if (hasChildren) {
    return <WrapperParentBlock {...props}>{children}</WrapperParentBlock>;
  }

  return <WrapperLeafBlock {...props}>{children}</WrapperLeafBlock>;
}

function WrapperLeafBlock({
  children,
  block,
  assistantTrigger,
}: BlockWrapperProps) {
  const depth = block.depth;
  const { getBlockHTMLId } = useResponseProjectDetailContext();
  const { isBlockContentUpdated } = useResponseProjectDetailContext();
  const isUpdated = isBlockContentUpdated(block.id);

  return (
    <Group
      id={getBlockHTMLId(block.id)}
      bg="white"
      p="03"
      position="apart"
      align="center"
      noWrap
      sx={theme => ({
        borderRadius: '12px',
        border:
          depth === 1
            ? `1px solid ${theme.colors.primary[1]}`
            : `1px solid ${theme.colors.gray[0]}`,
        ...(isUpdated && {
          backgroundColor: `${theme.colors.teal[0]} !important`,
          border: `1px solid ${theme.colors.teal[2]}`,
          borderRadius: theme.radius.sm,
          color: `${theme.colors.teal[9]} !important`,
          '& *': { color: `${theme.colors.teal[9]} !important` },
          '& button': { borderColor: `${theme.colors.teal[2]} !important` },
        }),
        '&:hover': {
          '& .title-wrapper-actions': {
            opacity: 1,
          },
        },
      })}
    >
      <Stack spacing="02" style={{ flex: 1 }}>
        <Group w="100%">
          <TitleWrapper
            block={block}
            isOpen={false}
            assistantTrigger={assistantTrigger}
          />
        </Group>
        {children}
      </Stack>
    </Group>
  );
}

function WrapperParentBlock({ children, block }: BlockWrapperProps) {
  const depth = block.depth;
  const stringId = block.id.toString();
  const theme = useMantineTheme();
  const { openedBlockIds, openBlocks, closeBlocks, getBlockHTMLId } =
    useResponseProjectDetailContext();

  const isOpen = openedBlockIds.includes(block.id);

  return (
    <Box w="100%" id={getBlockHTMLId(block.id)}>
      <Accordion
        value={isOpen ? stringId : null}
        onChange={value => {
          if (value) {
            openBlocks(block.getIdOfParentsAndSelf());
          } else {
            closeBlocks([block.id]);
          }
        }}
        chevron={
          <Stack
            justify="center"
            align="center"
            p="02"
            sx={theme => ({
              transition: 'all 0.2s ease-in-out',
              border: `1px solid ${theme.colors.gray[0]}`,
              borderRadius: theme.radius.md,
            })}
          >
            <IconChevronDown size="16px" color={theme.colors.gray[9]} />
          </Stack>
        }
        styles={{
          control: {
            backgroundColor: 'transparent !important',
            padding: theme.spacing['03'],
            paddingBottom: theme.spacing['02'],
            '&:hover': {
              '& .title-wrapper-actions': {
                opacity: 1,
              },
            },
          },
          label: {
            padding: 0,
          },
          item: {
            background: theme.white,
            borderRadius: '12px',
            border:
              depth === 1
                ? `1px solid ${theme.colors.primary[1]}`
                : `1px solid ${theme.colors.gray[0]}`,
          },
          content: {
            padding: theme.spacing['03'],
            paddingTop: 0,
          },
          chevron: {
            '& svg': {
              transition: 'all 0.3s ease-in-out',
            },
            '&[data-rotate]': {
              transform: 'rotate(0deg)',
              '& svg': {
                transform: 'rotate(180deg)',
              },
            },
          },
        }}
      >
        <Accordion.Item value={stringId}>
          <Accordion.Control>
            <TitleWrapper block={block} isOpen={isOpen} />
          </Accordion.Control>
          <Accordion.Panel>{children}</Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Box>
  );
}

function TitleWrapper({
  block,
  isOpen,
  assistantTrigger,
}: {
  block: ResponseProjectBlock;
  isOpen: boolean;
  assistantTrigger?: ReactNode;
}) {
  const theme = useMantineTheme();
  const depth = block.depth;
  const suffix = block.getBlockSuffix();
  const isCompleted = block.isCompleted() && !block.hasChildren();
  const { onOpenUpdateBlockModal, onOpenDeleteBlockModal } =
    useResponseProjectDetailContext();

  const handleEvent = (e: React.MouseEvent<SVGSVGElement>) => {
    e.stopPropagation();
  };

  const handleUpdate = (...e: Parameters<typeof handleEvent>) => {
    handleEvent(...e);
    onOpenUpdateBlockModal(block);
  };

  const handleDelete = (...e: Parameters<typeof handleEvent>) => {
    handleEvent(...e);
    onOpenDeleteBlockModal(block);
  };

  return (
    <Stack spacing="01" w="100%">
      <Group spacing="02" align="flex-start" position="apart" w="100%" noWrap>
        <BlockTitle
          isCompleted={isCompleted}
          depth={depth}
          title={`${suffix}. ${block.title}`}
          isOpen={isOpen}
          sideElement={
            <Group
              spacing="01"
              align="center"
              noWrap
              className="title-wrapper-actions"
              sx={{
                opacity: 0,
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <IconPencil
                size="16px"
                color={theme.colors.gray[8]}
                style={{ cursor: 'pointer' }}
                onClick={handleUpdate}
              />
              <IconTrash
                size="16px"
                color={theme.colors.gray[8]}
                style={{ cursor: 'pointer' }}
                onClick={handleDelete}
              />
            </Group>
          }
        />
        {assistantTrigger}
      </Group>
      <Description description={block.description} />
    </Stack>
  );
}

function Description({ description }: { description?: string }) {
  if (!description) {
    return null;
  }

  return (
    <Text size="sm" color="gray.6">
      {description}
    </Text>
  );
}
